import React from 'react';
import classNames from 'classnames';
import { types as sdkTypes } from '../../util/sdkLoader';
import { userLocation } from '../../util/maps';

import css from './LocationAutocompleteInput.module.css';
import { getGeocoderGooglePlacesAutocomplete, getGeocoderGooglePlacesDetails } from '../../util/api';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

export const CURRENT_LOCATION_ID = 'current-location';

// When displaying data from the Google Maps Places API, and
// attribution is required next to the results.
// See: https://developers.google.com/places/web-service/policies#powered
export const GeocoderAttribution = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.poweredByGoogle, className);
  return <div className={classes} />;
};

/**
 * A forward geocoding (place name -> coordinates) implementation
 * using the Google Maps Places API via direct HTTP requests.
 *
 * This implementation makes direct HTTPS requests to Google Places API
 */
class GeocoderGoogleMaps {
  constructor() {
    // Generate a simple session token for cost optimization
    // Format: timestamp-random
    this.sessionToken = null;
  }

  getSessionToken() {
    if (!this.sessionToken) {
      this.sessionToken = `${Date.now()}-${Math.random().toString(36).substring(7)}`;
    }
    return this.sessionToken;
  }

  clearSessionToken() {
    this.sessionToken = null;
  }

  // Public API
  //

  /**
   * Search places with the given name using Google Places Autocomplete API.
   * Makes request through backend proxy to avoid CORS issues.
   *
   * @param {String} search query for place names
   * @param {String|Array} countryLimit - country code(s) to limit search (e.g., 'FR' or ['FR', 'MC'])
   *
   * @return {Promise<{ search: String, predictions: Array<Object>}>}
   * results of the geocoding, should have the original search query
   * and an array of predictions.
   */
  getPlacePredictions(search, countryLimit) {
    console.log('[Google Places] Searching for:', search, 'with country limit:', countryLimit);

    // Use backend proxy endpoint to avoid CORS issues
    const params = new URLSearchParams({
      input: search,
      sessiontoken: this.getSessionToken(),
    });

    // Add country restrictions if provided
    // Format: country:fr|country:mc (each country needs its own prefix)
    if (countryLimit) {
      const countriesArray = Array.isArray(countryLimit) ? countryLimit : [countryLimit];
      const components = countriesArray
        .map(code => `country:${code.toLowerCase()}`)
        .join('|');
      params.append('components', components);
    }

    return getGeocoderGooglePlacesAutocomplete(params.toString())
      .then(data => {
        // API utility already returns parsed data
        if (data.status !== 'OK' && data.status !== 'ZERO_RESULTS') {
          console.error('[Google Places] API error:', data.status, data.error_message);
          throw new Error(`Google Places API error: ${data.status}`);
        }

        const predictions = data.predictions || [];

        return {
          search,
          predictions,
        };
      })
      .catch(error => {
        console.error('[Google Places] Error fetching predictions:', error);
      });
  }

  /**
   * Get the ID of the given prediction.
   */
  getPredictionId(prediction) {
    if (prediction.predictionPlace) {
      // default prediction defined above
      return prediction.id;
    }
    // prediction from Google Maps Places API
    return prediction.place_id;
  }

  /**
   * Get the address text of the given prediction.
   */
  getPredictionAddress(prediction) {
    if (prediction.predictionPlace) {
      // default prediction defined above
      return prediction.predictionPlace.address;
    }
    // prediction from Google Maps Places API
    return prediction.description;
  }

  /**
   * Fetch or read place details from the selected prediction.
   * Makes request through backend proxy to avoid CORS issues.
   *
   * @param {Object} prediction selected prediction object
   * @param {Number} currentLocationBoundsDistance distance for bounds calculation
   *
   * @return {Promise<util.propTypes.place>} a place object
   */
  getPlaceDetails(prediction, currentLocationBoundsDistance) {
    if (this.getPredictionId(prediction) === CURRENT_LOCATION_ID) {
      return userLocation().then(latlng => {
        return {
          address: '',
          origin: latlng,
          bounds: this.locationBounds(latlng, currentLocationBoundsDistance),
        };
      });
    }

    if (prediction.predictionPlace) {
      return Promise.resolve(prediction.predictionPlace);
    }

    // Use backend proxy endpoint to avoid CORS issues
    const params = new URLSearchParams({
      place_id: prediction.place_id,
      sessiontoken: this.getSessionToken(),
      fields: 'formatted_address,geometry,address_components',
    });

    return getGeocoderGooglePlacesDetails(params.toString())
      .then(data => {
        // Clear session token after getting details (completes the session)
        this.clearSessionToken();

        // API utility already returns parsed data
        if (data.status !== 'OK') {
          throw new Error(`Google Places Details API error: ${data.status}`);
        }

        const place = data.result;
        const location = place.geometry?.location;
        const viewport = place.geometry?.viewport;

        let bounds = null;
        if (viewport) {
          bounds = new SDKLatLngBounds(
            new SDKLatLng(viewport.northeast.lat, viewport.northeast.lng),
            new SDKLatLng(viewport.southwest.lat, viewport.southwest.lng)
          );
        } else if (location) {
          // Generate bounds if not provided
          bounds = this.locationBounds(
            new SDKLatLng(location.lat, location.lng),
            currentLocationBoundsDistance || 500
          );
        }

        return {
          address: place.formatted_address,
          origin: location ? new SDKLatLng(location.lat, location.lng) : null,
          bounds,
        };
      });
  }

  /**
   * Calculate a bounding box around a location
   */
  locationBounds(latlng, distance) {
    if (!latlng) {
      return null;
    }

    const earthRadius = 6371000; // Earth's radius in meters
    const latDelta = (distance / earthRadius) * (180 / Math.PI);
    const lngDelta = (distance / earthRadius) * (180 / Math.PI) / Math.cos(latlng.lat * Math.PI / 180);

    return new SDKLatLngBounds(
      new SDKLatLng(latlng.lat + latDelta, latlng.lng + lngDelta),
      new SDKLatLng(latlng.lat - latDelta, latlng.lng - lngDelta)
    );
  }
}

export default GeocoderGoogleMaps;
